import { SitesService } from '@shared/services/sites/sites.service';
import { computeInterval, Graph, TrafficHistogramType, TRAFIC } from '@shared/components/highcharts/graph/graph';
import _ from 'lodash';
import { BlockedIp, CountryGraphType, DateHistograms, LogsService, UrlStats } from '@shared/services/logs/logs.service';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@shared/services/auth.service';
import { Donut } from '@shared/components/highcharts/donut/donut';
import { firstValueFrom } from 'rxjs';
import { BySite, CountryData } from './dashboard.component';
import { DateRange } from '@shared/utils/date-range';
import { BaseLogRequest, CONTEXT, ContextEnum } from '@shared/models/ContextSiteModel';

export abstract class BaseDashboardComponent {
  lang: string;

  ctx: ContextEnum = CONTEXT;
  tableDataSize = 50;

  blockAttack: Highcharts.Options;

  totals: Record<TrafficHistogramType, number>;
  countryDatas?: Record<CountryGraphType, Array<CountryData>>;

  audience: Highcharts.Options;

  slowRequests: UrlStats[] = [];
  cpuConsumingRequests: UrlStats[] = [];
  topBlockedIps: BlockedIp[] = [];

  constructor(
    protected http: HttpClient,
    public sites: SitesService,
    protected logsService: LogsService,
    protected auth: AuthService,
    protected graph: Graph,
    protected donut: Donut,
  ) {
    this.lang = this.auth.getCurrentLanguage();
  }

  async getBlockAttack(request: BaseLogRequest) {
    this.blockAttack = null;

    const res = await firstValueFrom(this.logsService.getBlockedByAttackCategory(request, this.ctx));
    if (res?.datas) {
      this.blockAttack = this.donut.formatTypesAttacks(res.datas.filter((d) => d.uid));
    }
  }

  async getDateHistogram(request: BaseLogRequest): Promise<DateHistograms<TrafficHistogramType, BySite>> {
    let res = await firstValueFrom(
      this.logsService.getDateHistograms<TrafficHistogramType, BySite>(
        TRAFIC,
        {
          ...request,
          aggregationInterval: `${this.computeInterval(request)}s`,
        },
        this.ctx,
      ),
    );
    this.audience = this.graph.formatDataTrafic(res);
    this.totals = _.mapValues(res, (v) =>
      _(v.data)
        .map(([_, v]) => v)
        .sum(),
    );
    return res;
  }

  private computeInterval(request: BaseLogRequest): number {
    return computeInterval(new DateRange(request.fromDate, request.toDate));
  }

  async getOrigineAttaques(request: BaseLogRequest) {
    this.countryDatas = null;

    const res = await firstValueFrom(this.logsService.getTrafficByCountry(request, this.ctx));
    this.countryDatas = _.mapValues(res, (datas) => datas.map((p) => [p.name, p.y] as CountryData));
  }

  async getTopBlockedIps(request: BaseLogRequest) {
    this.topBlockedIps = await firstValueFrom(
      this.logsService.getTopBlockedIps(
        {
          ...request,
          aggSize: this.tableDataSize,
        },
        this.ctx,
      ),
    );
  }

  async getTopConsumingRequest(request: BaseLogRequest) {
    this.cpuConsumingRequests = await firstValueFrom(
      this.logsService.getCpuConsumingUrls(
        {
          ...request,
          aggSize: this.tableDataSize,
        },
        this.ctx,
      ),
    );
  }

  async getSlowRequest(request: BaseLogRequest) {
    this.slowRequests = await firstValueFrom(
      this.logsService.getSlowUrls(
        {
          ...request,
          aggSize: this.tableDataSize,
        },
        this.ctx,
      ),
    );
  }

  readonly MAP_COLOR_STOPS: Record<CountryGraphType, [number, string][]> = {
    traffic: [
      [0, '#d2efea'],
      [0.2, '#b3ebe0'],
      [0.4, '#93e6d5'],
      [0.6, '#6ee1c9'],
      [0.8, '#3fdbbc'],
    ],
    robot: [
      [0, '#fff4cc'],
      [0.2, '#ffeeb3'],
      [0.4, '#fee380'],
      [0.6, '#fed84d'],
      [0.8, '#fed330'],
    ],
    blockedByBrain: [
      [0, '#d2d100'],
      [0.2, '#e0b200'],
      [0.4, '#e99100'],
      [0.6, '#ed6c00'],
      [0.8, '#eb4112'],
    ],
    blockedCountry: [
      [0, '#F60'],
      [1e-30, '#666666'],
      [0.2, '#4D4D4D'],
      [0.4, '#333333'],
      [0.6, '#1A1A1A'],
      [0.8, '#000000'],
    ],
  };
}
